<template>
  <v-container>
    <v-row v-if="!failed" justify="center">
      <v-col cols="4">
        <v-card v-if="showFlashSale">
          <v-form ref="form" v-model="valid">
            <v-card-title class="headline" primary-title>
              <div class="mb-8">
                Sku filter
                <v-icon color="accent">mdi-arrow-down-circle</v-icon>
              </div>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="12" class="py-0">
                  <!-- Text field to handle multiple SKU entries -->
                  <v-text-field label="Enter TSKU (Press Enter for new value)" v-model="tskuInput" outlined dense
                    clearable :loading="loading" @keydown.enter="(e) => addTsku(e)"
                    placeholder="Enter TSKU and press Enter or paste a list separated by spaces or commas"></v-text-field>
                  <!-- Display chips for each entered TSKU -->
                  <v-chip-group v-if="SKU.length > 0" column>
                    <v-chip v-for="(item, index) in SKU" :key="index" closable @click:close="removeTsku(index)">
                      {{ item }}
                    </v-chip>
                  </v-chip-group>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="success" outlined @click="setFilters" :disabled="dontprogress">
                Continue
                <v-icon right color="success">mdi-arrow-right-bold</v-icon>
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card>
        <v-card flat v-if="loadingProducts">
          <v-card-text>
            <v-row justify="center" class="mt-16">
              <v-col cols="4">
                <h3 class="basic--text">Loading products...</h3>
                <v-progress-circular :size="70" color="accent" indeterminate></v-progress-circular>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- File Input Section -->
    <v-card flat v-if="showInputs && !loadingProducts && !showPriceEdit" v-model="valid" class="mt-12">
      <v-card-title>
        <v-row justify="center" class="basic--text">File info</v-row>
      </v-card-title>
      <v-form ref="form">
        <v-card-text>
          <v-row justify="center">
            <v-col cols="4">
              <v-text-field label="File name" v-model="filename" :rules="[rules.required]" filled
                clearable></v-text-field>
            </v-col>
          </v-row>
          <v-row justify="center">
            <v-col cols="4">
              <v-text-field label="Sheet title" v-model="sheetTitle" :rules="[rules.required]" filled
                clearable></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-row justify="end">
            <v-col cols="3">
              <v-btn color="error" outlined @click="backToFilter">
                <v-icon left color="error">mdi-arrow-left-bold</v-icon>
                Back to filter
              </v-btn>
            </v-col>
            <v-col cols="5">
              <v-btn color="success" outlined @click="getSpreadsheet" :disabled="filename.length === 0">
                Get file
                <v-icon right color="success">mdi-arrow-right-bold</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-form>
    </v-card>

    <!-- Loading State -->
    <v-card flat v-if="loadingFile" class="mt-12">
      <v-card-text>
        <v-row class="pb-2" justify="center">
          <v-img src="@/assets/loading.svg" max-width="200"></v-img>
        </v-row>
        <v-row justify="center">
          <h3 class="basic--text">Please wait, the file is being created...</h3>
        </v-row>
        <v-row justify="center">
          <v-col cols="6">
            <v-progress-linear indeterminate color="accent"></v-progress-linear>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <StatusInfo v-if="failed" :responseMessage="responseMessage" :filename="'feeling_blue.svg'" :pending="false"
      :textColor="'error--text'" @close="closeError"></StatusInfo>

    <!-- File Response Section -->
    <v-card flat v-if="fileResponse" class="mt-12">
      <v-card-text>
        <v-row class="pb-2" justify="center">
          <v-img :src="require(`@/assets/${responseImg}`)" max-width="300"></v-img>
        </v-row>
        <v-row justify="center" class="mt-10">
          <h2 :class="textColor">{{ responseText }}</h2>
        </v-row>
        <template v-if="fileCreationSuccess">
          <v-row justify="center" class="mt-10">
            <v-btn outlined color="success" @click="openFile">Open file</v-btn>
          </v-row>
          <v-row justify="center" class="mt-10">
            <h3 class="basic--text">Or copy link: <br />{{ fileLink }}</h3>
          </v-row>
          <v-row justify="center" class="mt-10">
            <h3 class="basic--text">Want to generate another file?</h3>
          </v-row>
          <v-row justify="center" class="mt-10">
            <h3 class="basic--text">Click the button below:</h3>
          </v-row>
        </template>
        <v-row justify="center" class="mt-10">
          <v-btn outlined color="accent" @click="generateNew">
            Generate new
            <v-icon right>mdi-file-plus</v-icon>
          </v-btn>
        </v-row>
      </v-card-text>
    </v-card>
    <SkusWholesalePriceEdit v-if="showPriceEdit && !showSizeOrder && !loadingProducts" :products="products"
      @getSizes="getSizes" @backToFilter="backToFilterPriceEdit"></SkusWholesalePriceEdit>

    <OrderSizes v-if="showSizeOrder" :items="productsArray" @orderedSizes="afterSizeOrderSizes"
      @backToFilter="backToFilterSizes"></OrderSizes>

  </v-container>
</template>

<script>
import StatusInfo from "../organisms/StatusInfo";
import SkusWholesalePriceEdit from "./SkusWholesalePriceEdit.vue";
import OrderSizes from "../FlashSales/OrderSizes.vue";

export default {
  name: "FlashSaleSingleFilter",
  components: {
    StatusInfo,
    SkusWholesalePriceEdit,
    OrderSizes
  },
  data: () => ({
    responseMessage: "",
    failed: false,
    showFlashSale: true,
    tskuInput: "",
    fileNameExist:false,
    SKU: [],
    loading: false,
    loadingProducts: false,
    showInputs: false,
    loadingFile: false,
    removeInit: false,
    valid: false,
    filename: "",
    sheetTitle: "",
    products: [],
    fileResponse: false,
    responseText: "",
    fileLink: "",
    fileCreationSuccess: false,
    responseImg: "",
    textColor: "",
    showPriceEdit: false,
    showSizeOrder: false,
    productsArray: [],
    orderedSizes: [],
    dontprogress : true,

    rules: {
      required: (value) => !!value || "Field is required.",
    },
    watch: {
    SKU: {
      handler(newVal) {
        this.valid = newVal.length > 0; 
      },
      immediate: true, 
      deep: true, 
    },
  }
  }),
  methods: {
    // Add the entered TSKU to the list and clear the input
    addTsku(e) {
      e.preventDefault();
      this.dontprogress = false;
      const trimmedTsku = this.tskuInput.trim();

      if (trimmedTsku) {
        // Split the input by spaces or commas to handle multiple SKUs
        const skus = trimmedTsku.split(/[\s,]+/).filter((sku) => sku.trim() !== "");

        skus.forEach((sku) => {
          if (!this.SKU.includes(sku)) {
            this.SKU.push(sku);
          }
        });

        this.tskuInput = ""; // Clear input field after adding
      }
    },
    backToFilterPriceEdit() {
      this.showFlashSale = true;
      this.showPriceEdit = false;
      this.showInputs = false;
      this.SKU = [];
      this.tskuInput = "";
      this.dontprogress = true;
    },
    afterSizeOrderSizes(sizes) {
      this.orderedSizes = sizes;
      this.showInputs = true;
      this.showPriceEdit = false;
      this.showSizeOrder = false;
    },
    backToFilterSizes() {
      this.showFlashSale = false;
      this.showSizeOrder = false;
      this.showPriceEdit = true;
      this.showInputs = false;
    },
    // Remove TSKU from the list
    removeTsku(index) {
      this.SKU.splice(index, 1);
    },
    getSizes() {
      this.productsArray = this.products;
      // this.showPriceEdit = false;
      this.showSizeOrder = true;
    },
    // backToFilter() {
    //   this.showPriceEdit = false;
    //   this.showSizeOrder = false;
    //   this.showInputs = false;
    //   this.showFilter = true;
    //   this.fileResponse = false;
    // },
    async setFilters() {
      if (this.$refs.form.validate()) {
        const filters = { SKU: this.SKU };
        this.getProducts(filters);
        this.showFlashSale = false;
        this.showPriceEdit = true;

      }
    },
    async fillFormData() {
      let formData = new FormData();

      formData.append("spreadSheetTitle", this.filename);

      formData.append("sheetTitle", this.sheetTitle);

      for (let i in this.orderedSizes) {
        formData.append("sizeOrder", this.orderedSizes[i]);
      }

      for (let indx in this.productsArray) {
        formData.append("products", JSON.stringify(this.productsArray[indx]));
      }

      if (this.brands != "") {
        for (let j in this.brands) {
          formData.append("brands", this.brands[j]);
        }
      }
      if (this.genders != "") {
        for (let j in this.genders) {
          formData.append("gender", this.genders[j]);
        }
      }

      if (this.categories) {
        for (let y in this.categories) {
          formData.append("categories", this.categories[y]);
        }
      }

      formData.append("condition", this.condition);

      return formData;
    },
    async getSpreadsheet() {
      // this.showInputs = false;
      // this.loadingFile = true;
      this.showInputs = false;
      this.loadingFile = true;
      this.loadingProducts = false;

      let data = await this.fillFormData();

      try {
        const response = await this.$store.dispatch("getSpreadsheet", data);
        this.textColor = "basic--text";
        this.responseImg = "completed.svg";
        this.loadingFile = false;
        this.fileResponse = true;
        this.fileCreationSuccess = true;

        const link = response.data;
        this.fileLink = link;
        window.open(link);

        this.responseText = `Success! File ${this.filename} created!`;
      } catch (error) {
        console.log(error);

        this.textColor = "red--text";
        this.responseImg = "empty.svg";
        this.loadingFile = false;
        this.fileResponse = true;
        this.responseText = error.response.data
          ? error.response.data
          : "Couldn't create file.";
        this.fileCreationSuccess = false;
      }
      this.filename = "";
      this.sheetTitle = "";
    },
    openFile() {
      window.open(this.fileLink);
    },
    async getProducts(filters) {
      this.loadingProducts = true;
      let token = localStorage.getItem("token");
      try {
        const response = await fetch(
          "https://bc-02.cookiejartech.lt/productsdb/api/products/findSkus",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ SKU: filters.SKU }),
          }
        );

        if (!response.ok) {
          this.showPriceEdit = false
          throw new Error(`Products don't exist ${response.status}`);
        }
        
        const result = await response.json();
        this.showInputs = false;
        this.loadingProducts = false;
        this.products = result.products;
        console.log("=>products", this.products);
        console.log("=>", result);
        this.$emit("getProducts", result); // Emit the response data to parent
        this.showInputs = true;
        this.tskuInput = "";
        this.SKU = "";
      } catch (error) {
        this.loadingProducts = false;
        this.failed = true;
        this.responseMessage = error.message || "Sorry, something went wrong...";
      }
    },
    async getFileLink() {
      let token = localStorage.getItem("token");
      const products = this.products;
      try {
        const response = await fetch(
          "https://bc-02.cookiejartech.lt/productsdb/api/flashSales/skusflashsalefile",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              products: [products],
              SpreadSheetTitle: this.filename,
              SheetTitle: this.sheetTitle,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        console.log("this is response", response);
        this.loadingFile = false;
        const result = await response.text();
        console.log("=>", result);
        this.fileLink = result;
        this.fileResponse = true;
        this.responseText = `Success! File ${this.filename} created!`;
        this.responseImg = "completed.svg";
        this.fileCreationSuccess = true;
        console.log("=> File Generated here is the LINK ", result);
      } catch (error) {
        this.loadingProducts = false;
        this.failed = true;
        this.responseMessage = error.message || "Sorry, something went wrong...";
      }
    },
    serialize(obj) {
      let str = [];
      for (let p in obj) {
        str.push(p + "=" + encodeURIComponent(obj[p]));
      }
      return str.join("&");
    },
    async getDropdowns() {
      this.loading = true;
      try {
        const response = await this.$store.dispatch("getBrands");
        this.brands = response.data;
        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },
    // async getSpreadsheet() {
    //   this.showInputs = false;
    //   this.loadingFile = true;
    //   this.loadingProducts = false;
    //   this.getFileLink();
    // },
    generateNew() {
      this.fileResponse = false;
      this.showFlashSale = true;
      this.SKU = [];
      this.tskuInput ="";
      this.dontprogress = true;
    },
    closeError() {
      this.failed = false;
      this.tskuInput = "";
      this.showFlashSale = true;
      this.SKU = [];
      this.dontprogress = true
    },
    backToFilter() {
      this.showInputs = false;
      this.showFlashSale = true;
      this.tskuInput = ""; // Reset the input field
      this.SKU = []; // Clear the SKU list if necessary
    },
  },
  async mounted() {
    await this.getDropdowns();
  },
};
</script>